import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { InputText } from 'primereact/inputtext'
import { MultiSelect } from 'primereact/multiselect'
import { SelectButton } from 'primereact/selectbutton'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CommentsDrawer } from '~/components/CommentsDrawer'
import DocumentCreateForm from '~/components/DocumentCreateForm'
import DocumentDetailsDrawer from '~/components/DocumentDetailsDrawer'
import { useLanguage } from '~/contexts/LanguageContext'
import { useDepartmentsList } from '~/hooks/departmentsApi'
import { useDocumentList } from '~/hooks/documentsApi'
import { useUsersList } from '~/hooks/usersApi'
import { useUserStore } from '~/stores/userStore'
import { DepartmentRole } from '~/types/department.types'
import type { Document } from '~/types/document.types'
import { DocumentLiveStatus, DocumentStagingStatus } from '~/types/document.types'
import { enumToOptions, LiveStatusLabels, StagingStatusLabels } from '~/types/feEnumLabels'
import { UserRole } from '~/types/user.types'

const StagingStatus = ({ status }: { status: DocumentStagingStatus }) => {
  switch (status) {
    case DocumentStagingStatus.PendingUpload:
      return (
        <Chip label='Pending Upload' className='text-nowrap text-[10px] font-bold bg-red-300 h-5' />
      )
    case DocumentStagingStatus.Draft:
      return <Chip label='Draft' className='text-nowrap text-[10px] font-bold bg-red-300 h-5' />
    case DocumentStagingStatus.InternalReview:
      return (
        <Chip
          label='Internal Review'
          className='text-nowrap text-[10px] font-bold bg-amber-300 h-5'
        />
      )
    case DocumentStagingStatus.ConsultantReview:
      return (
        <Chip
          label='Consultant Review'
          className='text-nowrap text-[10px] font-bold bg-cyan-300 h-5'
        />
      )
    case DocumentStagingStatus.AuditReady:
      return (
        <Chip label='Audit Ready' className='text-nowrap text-[10px] font-bold bg-lime-300 h-5' />
      )
    case DocumentStagingStatus.Final:
      return <Chip label='Final' className='text-nowrap text-[10px] font-bold bg-blue-300 h-5' />
  }
  return <Chip className='text-[10px] font-bold h-5' label='Unknown' />
}

const LiveStatus = ({ status }: { status: DocumentLiveStatus }) => {
  switch (status) {
    case DocumentLiveStatus.Staging:
      return <Chip label='Staging' className='text-nowrap text-[10px] font-bold bg-rose-300 h-5' />
    case DocumentLiveStatus.Live:
      return <Chip label='Live' className='text-nowrap text-[10px] font-bold bg-blue-300 h-5' />
    case DocumentLiveStatus.Archived:
      return <Chip label='Archived' className='text-nowrap text-[10px] font-bold bg-zinc-300 h-5' />
  }
  return <Chip className='text-[10px] font-bold h-5' label='Unknown' />
}

const DocumentsPage = () => {
  const user = useUserStore((s) => s.user)
  const [search, setSearch] = useState('')
  const [deptFilter, setDeptFilter] = useState<number[] | undefined>()
  const [stagingFilter, setStagingFilter] = useState<DocumentStagingStatus[] | undefined>()
  const [liveFilter, setLiveFilter] = useState<DocumentLiveStatus[] | undefined>()
  const [picUserIds, setPicUserIds] = useState<number[] | undefined>()
  const [onlyMyDocuments, setOnlyMyDocuments] = useState(
    user?.departments?.some((d) => d.role === DepartmentRole.Admin) ?? false,
  )
  const [onlyMyDepartments, setOnlyMyDepartments] = useState(
    (user?.role ?? UserRole.Viewer) < UserRole.Admin,
  )
  const [pendingOfficiation, setPendingOfficiation] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const [activeDocumentId, setActiveDocumentId] = useState<number | null>(null)
  const [activeCommentTarget, setActiveCommentTarget] = useState<Document | null>(null)

  const documentIdParam = searchParams.get('documentId')
  const showCommentsParam = searchParams.get('showComments')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const { lang, setLang } = useLanguage()

  const documentsQuery = useDocumentList({
    search: search.length > 2 ? search : undefined,
    departmentIds: deptFilter && deptFilter.length > 0 ? deptFilter : undefined,
    stagingStatuses: stagingFilter && stagingFilter.length > 0 ? stagingFilter : undefined,
    liveStatuses: liveFilter && liveFilter.length > 0 ? liveFilter : undefined,
    picUserIds,
    onlyMyDocuments,
    onlyMyDepartments,
    includeControls: true,
    pendingOfficiation: pendingOfficiation ? true : undefined,
    pageSize: 100,
  })

  const departmentsQuery = useDepartmentsList()
  const usersQuery = useUsersList()

  const renderDocNumber = (d: Document) => {
    return d.liveDocNumber || d.docNumberTemplate || '-'
  }

  const renderDepartment = (d: Document) => {
    const dept = departmentsQuery.data?.find((dp) => dp.id === d.departmentId)
    return dept ? dept.title : `ID: ${d.departmentId}`
  }

  const renderPIC = (d: Document) => {
    const user = usersQuery.data?.find((u) => u.id === d.picUserId)
    return user ? user.name : d.picUserId ? `ID: ${d.picUserId}` : '-'
  }
  const renderTitle = (d: Document) => (lang === 'id' && d.titleId ? d.titleId : d.title)

  useEffect(() => {
    if (documentIdParam) {
      const docId = Number.parseInt(documentIdParam, 10)
      if (!Number.isNaN(docId)) {
        if (showCommentsParam === 'true') {
          const doc = documentsQuery.data?.data?.find((d) => d.id === docId)
          if (doc) {
            setActiveCommentTarget(doc)
          }
        } else {
          setActiveDocumentId(docId)
        }
      }
    }
  }, [documentIdParam, showCommentsParam, documentsQuery.data])

  return (
    <div className='p-4'>
      <div className='flex flex-wrap gap-4 justify-between mb-2 items-center'>
        <div>
          <h1 className='text-3xl'>Documents</h1>
        </div>
        <div className='flex gap-2 items-center'>
          <Button
            label='New Document'
            icon='pi pi-plus'
            severity='success'
            onClick={() => setIsCreateOpen(true)}
            className='h-9'
          />
          <Button
            icon='pi pi-refresh'
            onClick={() => documentsQuery.refetch()}
            loading={documentsQuery.isFetching}
            className='h-9'
          />
          <SelectButton
            options={[
              { label: 'En', value: 'en' },
              { label: 'Id', value: 'id' },
            ]}
            value={lang}
            onChange={(e) => e.value && setLang(e.value)}
            pt={{
              root: { className: 'flex flex-nowrap' },
              button: { className: 'h-9 py-0 px-3 text-sm flex items-center justify-center' },
            }}
          />
        </div>
      </div>

      <div className='flex flex-wrap gap-2 items-center mb-6'>
        <IconField iconPosition='right' className='grow min-w-44'>
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search title/code...'
            className='h-9 w-full text-sm flex items-center'
            pt={{ root: { className: 'py-0' } }}
          />
          <InputIcon className='pi pi-search'> </InputIcon>
        </IconField>
        <Button
          label={onlyMyDepartments ? 'My Departments' : 'All Departments'}
          icon={onlyMyDepartments ? 'pi pi-envelope' : 'pi pi-globe'}
          severity={onlyMyDepartments ? 'info' : 'secondary'}
          onClick={() => setOnlyMyDepartments(!onlyMyDepartments)}
          className='h-9 text-sm px-3'
        />

        {user?.role === UserRole.Officiator && (
          <Button
            label='Officiating'
            icon='pi pi-verified'
            severity={pendingOfficiation ? 'info' : 'secondary'}
            onClick={() => setPendingOfficiation(!pendingOfficiation)}
            className='h-9 text-sm px-3'
          />
        )}

        <Button
          label={onlyMyDocuments ? 'My Documents' : 'All Documents'}
          icon={onlyMyDocuments ? 'pi pi-envelope' : 'pi pi-globe'}
          severity={onlyMyDocuments ? 'info' : 'secondary'}
          onClick={() => setOnlyMyDocuments(!onlyMyDocuments)}
          className='h-9 text-sm px-3'
        />

        {(user?.role ?? UserRole.Viewer) >= UserRole.Admin && (
          <MultiSelect
            value={picUserIds}
            options={usersQuery.data}
            onChange={(e) => setPicUserIds(e.value)}
            optionLabel='name'
            optionValue='id'
            placeholder='PICs'
            className='h-9 grow md:flex-initial md:w-36 text-sm'
            maxSelectedLabels={1}
            selectedItemsLabel='{0} PICs'
            pt={{
              label: { className: 'py-0 h-full flex items-center' },
              trigger: { className: 'h-full flex items-center' },
            }}
            tooltip='Filter by Document PIC'
            tooltipOptions={{
              position: 'top',
            }}
          />
        )}

        <MultiSelect
          value={deptFilter}
          options={departmentsQuery.data}
          onChange={(e) => setDeptFilter(e.value)}
          optionLabel='title'
          optionValue='id'
          placeholder='Departments'
          className='h-9 grow md:flex-initial md:w-44 text-sm'
          maxSelectedLabels={1}
          selectedItemsLabel='{0} Depts'
          pt={{
            label: { className: 'py-0 h-full flex items-center' },
            trigger: { className: 'h-full flex items-center' },
          }}
          tooltip='Filter by department'
          tooltipOptions={{
            position: 'top',
          }}
        />
        <MultiSelect
          value={stagingFilter}
          options={enumToOptions(StagingStatusLabels)}
          onChange={(e) => setStagingFilter(e.value)}
          placeholder='Staging Status'
          className='h-9 grow md:flex-initial md:w-44 text-sm'
          maxSelectedLabels={1}
          selectedItemsLabel='{0} Staging'
          pt={{
            label: { className: 'py-0 h-full flex items-center' },
            trigger: { className: 'h-full flex items-center' },
          }}
          tooltip='Filter by staging status'
          tooltipOptions={{
            position: 'top',
          }}
        />
        <MultiSelect
          value={liveFilter}
          options={enumToOptions(LiveStatusLabels)}
          onChange={(e) => setLiveFilter(e.value)}
          placeholder='Live Status'
          className='h-9 grow md:flex-initial md:w-40 text-sm'
          maxSelectedLabels={1}
          selectedItemsLabel='{0} Live'
          pt={{
            label: { className: 'py-0 h-full flex items-center' },
            trigger: { className: 'h-full flex items-center' },
          }}
          tooltip='Filter by live status'
          tooltipOptions={{
            position: 'top',
          }}
        />
      </div>

      <DataTable
        key={lang}
        value={documentsQuery.data?.data ?? []}
        loading={documentsQuery.isLoading}
        onRowClick={(e) => setActiveDocumentId(e.data.id)}
        pt={{ bodyRow: { className: 'cursor-pointer hover:bg-gray-50' } }}
        emptyMessage='No documents found.'
        className='text-sm'
        rowHover
      >
        <Column
          header='Doc #'
          body={renderDocNumber}
          className='font-mono'
          style={{ width: '150px' }}
        />
        <Column field='title' header='Title' body={renderTitle} />
        <Column header='Dept' body={renderDepartment} />
        <Column header='PIC' body={renderPIC} />
        <Column
          header='Controls'
          body={(d: Document) => `${d.controlDocumentMap?.length ?? 0} controls`}
        />
        <Column
          header='Staging'
          body={(d) => (
            <div className='flex items-center gap-1'>
              <StagingStatus status={d.stagingStatus} />
              <Button
                icon='pi pi-comments'
                rounded
                text
                className='h-7 w-7'
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveCommentTarget(d)
                }}
              />
            </div>
          )}
        />
        <Column header='Live' body={(d) => <LiveStatus status={d.liveStatus} />} />
      </DataTable>

      <CommentsDrawer
        visible={!!activeCommentTarget}
        onHide={() => {
          setActiveCommentTarget(null)
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.delete('documentId')
            next.delete('showComments')
            return next
          })
        }}
        document={activeCommentTarget || undefined}
      />

      <DocumentDetailsDrawer
        documentId={activeDocumentId}
        onHide={() => {
          setActiveDocumentId(null)
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.delete('documentId')
            return next
          })
        }}
      />

      <Dialog
        header='Create New Document'
        visible={isCreateOpen}
        onHide={() => setIsCreateOpen(false)}
        className='w-full max-w-xl'
      >
        <DocumentCreateForm
          onSuccess={() => {
            setIsCreateOpen(false)
            documentsQuery.refetch()
          }}
          onCancel={() => setIsCreateOpen(false)}
        />
      </Dialog>
    </div>
  )
}

export default DocumentsPage
